import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useSystemTableConfig } from "@/hooks/use-system-table-config.hook.ts";
import { ApiTableTypes } from "@/types/custom-field.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiEmployeeTerminationFull } from "@/types/employees/employee-termination.types.ts";
import { EmployeeFieldPermission } from "@/types/role.types.ts";
import { Modal, ModalProps, Stack, Text } from "@skailer-com/uikit";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface Props extends ModalProps {
  employee: ApiEmployee;
  termination: ApiEmployeeTerminationFull;
}

const InfoRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap="xs">
    <Text className="text-secondary">{label}</Text>
    <div className="text-title whitespace-pre-line">{children}</div>
  </Stack>
);

export function EmployeeTerminationInfoModal({ ...props }: Props) {
  const { employee, termination } = props;
  const { t } = useTranslation();
  const { canViewField, canViewCustomTableFieldForEmployee } = usePermissions();
  const { renderDate, renderEmployeeName } = useFormatter();
  const { renderFieldDataInCell } = useCustomFieldsHook();
  const { table: terminationTable, customFields: terminationCustomFields } = useSystemTableConfig(
    ApiTableTypes.employee_terminations,
    "view"
  );

  return (
    <Modal
      {...props}
      title={t("core:employment_ended")}
      variant={"middle"}
      layout={"base"}
      size={"md"}
      withCloser={true}
    >
      <Stack gap="md">
        {termination.termination_leave_type && (
          <InfoRow label={t("core:leave_type")}>{termination.termination_leave_type.title}</InfoRow>
        )}

        {termination.date_last_working && (
          <InfoRow label={t("core:last_working_day")}>
            {renderDate(termination.date_last_working)}
          </InfoRow>
        )}

        {canViewField(employee, EmployeeFieldPermission.employee_termination_reason) &&
          termination.termination_reason?.title && (
            <InfoRow label={t("core:termination_reason")}>
              {termination.termination_reason.title}
            </InfoRow>
          )}

        {termination.eligible_for_rehire !== undefined && (
          <InfoRow label={t("core:eligible_for_rehire")}>
            {termination.eligible_for_rehire ? t("common:yes") : t("common:no")}
          </InfoRow>
        )}

        {termination.replacement_employee && (
          <InfoRow label={t("core:termination_handover_to")}>
            {renderEmployeeName(termination.replacement_employee)}
          </InfoRow>
        )}

        {canViewField(employee, EmployeeFieldPermission.employee_termination_comment) &&
          termination.comment && (
            <InfoRow label={t("core:termination_comment")}>{termination.comment}</InfoRow>
          )}

        {terminationCustomFields.map((field) => {
          if (
            !terminationTable ||
            !canViewCustomTableFieldForEmployee(employee, terminationTable.id, field.slug)
          ) {
            return null;
          }

          const value = renderFieldDataInCell(field, termination.custom_fields ?? []);
          if (!value) return null;

          return (
            <InfoRow key={field.id} label={field.title}>
              {value}
            </InfoRow>
          );
        })}
      </Stack>
    </Modal>
  );
}
